import React, { useState } from 'react';
import { Plus, Check, X, Sparkles, AlertCircle } from 'lucide-react';
import { config } from '../utils/config';
import { apiClient } from '../api/client';
import { friendlyFieldName } from '../utils/entityLabels';

interface AddEntityWidgetProps {
  documentId: number;
  onEntityAdded?: () => void;
}

/**
 * AddEntityWidget - Allows users to manually add missing entities
 * These entities feed back into the learning system to improve future extractions
 */
const AddEntityWidget: React.FC<AddEntityWidgetProps> = ({ documentId, onEntityAdded }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [entityLabel, setEntityLabel] = useState('BUYER_NAME');
  const [entityValue, setEntityValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Comprehensive entity labels organized by category (same as EntityCorrectionWidget)
  const entityLabelsByCategory = {
    'Invoice Details': [
      'INVOICE_NUMBER',
      'INVOICE_DATE',
      'DUE_DATE',
      'PO_NUMBER',
      'REFERENCE_NUMBER'
    ],
    'Parties': [
      'SELLER_NAME',
      'SELLER_ADDRESS',
      'SELLER_EMAIL',
      'SELLER_PHONE',
      'BUYER_NAME',
      'BUYER_ADDRESS',
      'BUYER_EMAIL',
      'BUYER_PHONE'
    ],
    'Financial': [
      'TOTAL_AMOUNT',
      'SUBTOTAL',
      'TAX_AMOUNT',
      'DISCOUNT',
      'PAYMENT_TERMS',
      'PAYMENT_METHOD',
      'CURRENCY'
    ],
    'Shipping & Additional': [
      'SHIPPING_COST',
      'NOTES'
    ],
    'Other': [
      'LINE_ITEM',
      'PRODUCT',
      'QUANTITY',
      'UNIT_PRICE',
      'OTHER'
    ]
  };

  const handleSubmit = async () => {
    if (!entityValue.trim()) {
      setError('Entity value cannot be empty');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await apiClient.post(`${config.endpoints.corrections}/create-entity`, {
        document_id: documentId,
        entity_label: entityLabel,
        entity_value: entityValue.trim(),
        // No confidence: a value the user typed has no extraction confidence.
        // The backend stores NULL and marks it manually_created.
        context: ''
      });

      setSuccess(true);
      setEntityValue('');

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
        setIsAdding(false);
      }, 3000);

      // Notify parent to refresh entities
      if (onEntityAdded) {
        onEntityAdded();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add entity');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEntityValue('');
    setError(null);
    setIsAdding(false);
  };

  // Success state
  if (success) {
    return (
      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl space-y-3">
        <div className="flex items-center gap-2">
          <Check className="w-5 h-5 text-green-400" />
          <span className="text-sm font-medium text-green-300">
            Entity Added Successfully!
          </span>
        </div>

        <div className="flex items-start gap-2 p-3 bg-coral-500/10 rounded-lg">
          <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
          <div className="text-xs">
            <p className="text-purple-700 dark:text-purple-300 font-medium mb-1">System is learning:</p>
            <ul className="text-stone-600 dark:text-foreground-secondary space-y-0.5">
              <li>• Entity saved: <span className="font-semibold">{entityLabel}</span> = "{entityValue}"</li>
              <li>• Will appear in future extractions</li>
              <li>• Pattern learned for similar documents</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Collapsed state (button only)
  if (!isAdding) {
    return (
      <div className="flex justify-center">
        <button
          onClick={() => setIsAdding(true)}
          className="group flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-xl hover:from-green-500/30 hover:to-emerald-500/30 hover:border-green-500/60 transition-all shadow-lg hover:shadow-green-500/20"
        >
          <Plus className="w-5 h-5 text-green-400 group-hover:rotate-90 transition-transform" />
          <span className="text-sm font-semibold text-green-300">Add Missing Entity</span>
          <Sparkles className="w-4 h-4 text-green-400 opacity-70" />
        </button>
      </div>
    );
  }

  // Adding state (form)
  return (
    <div className="p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Plus className="w-5 h-5 text-green-400" />
        <h3 className="text-sm font-semibold text-green-100">Add Missing Entity</h3>
      </div>

      <p className="text-xs text-green-200/80">
        Add an entity that was missed during extraction. The system will learn from this and extract it automatically in future documents.
      </p>

      {/* Entity Type Selection */}
      <div>
        <label className="block text-xs text-green-300 mb-1.5 font-medium">What kind of detail?</label>
        <select
          value={entityLabel}
          onChange={(e) => setEntityLabel(e.target.value)}
          className="w-full px-3 py-2 bg-coral-900/30 border border-green-500/30 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
        >
          {Object.entries(entityLabelsByCategory).map(([category, labels]) => (
            <optgroup key={category} label={category}>
              {labels.map((label: string) => (
                <option key={label} value={label}>{friendlyFieldName(label)}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Entity Value Input */}
      <div>
        <label className="block text-xs text-green-300 mb-1.5 font-medium">Value:</label>
        <input
          type="text"
          value={entityValue}
          onChange={(e) => setEntityValue(e.target.value)}
          className="w-full px-3 py-2 bg-coral-900/30 border border-green-500/30 rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
          placeholder="Enter the entity value..."
          autoFocus
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !entityValue.trim()}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500/20 border border-green-500/40 text-green-300 rounded-lg hover:bg-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
              <span className="text-sm">Adding...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span className="text-sm">Add Entity</span>
            </>
          )}
        </button>

        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          className="px-4 py-2.5 bg-coral-500/10 border border-coral-500/30 text-foreground-muted rounded-lg hover:bg-coral-500/20 transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Help Text */}
      <div className="flex items-start gap-2 p-3 bg-coral-500/10 border border-coral-500/20 rounded-lg">
        <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
        <p className="text-xs text-stone-600 dark:text-foreground-secondary">
          <strong>Learning System:</strong> When you add this entity, the system learns the pattern and will automatically extract similar entities from future documents.
        </p>
      </div>
    </div>
  );
};

export default AddEntityWidget;
